import { useParams, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { CandidateLayout } from "@/components/candidate/CandidateLayout";
import { BannerSection } from "@/components/partner/BannerSection";
import { HeroSection } from "@/components/partner/HeroSection";
import { AboutSection } from "@/components/partner/AboutSection";
import { BenefitsSection } from "@/components/partner/BenefitsSection";
import { CTASection } from "@/components/partner/CTASection";
import { CountdownSection } from "@/components/partner/CountdownSection";
import { PageLoader } from "@/components/common/PageLoader";
import { Helmet } from "react-helmet-async";
import { useAuthUser } from "@/hooks/useAuth";
import { useNoIndex } from "@/hooks/useCanonicalTag";

interface ButtonData {
  text: string;
  url: string;
  variant: "primary" | "outline";
}

interface Section {
  id: string;
  section_type: string;
  title: string;
  subtitle: string;
  content: string;
  youtube_url: string;
  image_url: string;
  background_color: string;
  buttons: ButtonData[];
  is_visible: boolean;
  order_index: number;
}

export default function PartnerLanding() {
  useNoIndex();
  const { slug } = useParams<{ slug: string }>();
  const { data: user } = useAuthUser();
  const viewTracked = useRef(false);

  const { data: page, isLoading: pageLoading } = useQuery({
    queryKey: ["partner-page", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partner_landing_pages")
        .select("*")
        .eq("slug", slug!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  const { data: sections = [], isLoading: sectionsLoading } = useQuery({
    queryKey: ["partner-sections", page?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partner_landing_sections")
        .select("*")
        .eq("landing_page_id", page!.id)
        .order("order_index");
      if (error) throw error;
      return (data as unknown as Section[]) || [];
    },
    enabled: !!page?.id,
  });

  // Track page view
  useEffect(() => {
    if (page?.id && !viewTracked.current) {
      viewTracked.current = true;
      supabase.from("partner_page_views").insert({
        landing_page_id: page.id,
        user_id: user?.id || null,
      }).then(() => {});
    }
  }, [page?.id, user?.id]);

  // Track button click
  const handleButtonClick = useCallback((url: string, text: string) => {
    if (!page?.id) return;
    supabase.from("partner_button_clicks").insert({
      landing_page_id: page.id,
      button_url: url,
      button_text: text,
      user_id: user?.id || null,
    }).then(() => {});
  }, [page?.id, user?.id]);

  if (pageLoading || sectionsLoading) return <CandidateLayout><PageLoader /></CandidateLayout>;
  if (!page) return <Navigate to="/candidate/dashboard" replace />;

  const visibleSections = sections.filter(s => s.is_visible);

  const renderSection = (section: Section) => {
    const buttons = (Array.isArray(section.buttons) ? section.buttons : []) as ButtonData[];
    const props = {
      title: section.title || "",
      subtitle: section.subtitle || "",
      content: section.content || "",
      buttons,
      backgroundColor: section.background_color || undefined,
    };

    switch (section.section_type) {
      case "banner":
        return <BannerSection key={section.id} imageUrl={section.image_url} buttons={buttons} backgroundColor={section.background_color || undefined} onButtonClick={handleButtonClick} />;
      case "countdown":
        return <CountdownSection key={section.id} title={section.title || ""} targetDate={section.content || ""} backgroundColor={section.background_color || undefined} />;
      case "hero":
        return <HeroSection key={section.id} {...props} youtubeUrl={section.youtube_url} onButtonClick={handleButtonClick} />;
      case "about":
        return <AboutSection key={section.id} {...props} imageUrl={section.image_url} />;
      case "benefits":
        return <BenefitsSection key={section.id} {...props} />;
      case "cta":
        return <CTASection key={section.id} {...props} onButtonClick={handleButtonClick} />;
      default:
        return null;
    }
  };

  return (
    <CandidateLayout>
      <Helmet>
        <title>{page.page_title || "Parceiros"}</title>
        {page.meta_description && <meta name="description" content={page.meta_description} />}
      </Helmet>
      <div className="w-full">
        {visibleSections.map(renderSection)}
      </div>
    </CandidateLayout>
  );
}
